// ---------------------------------------------------------------------------
// ExpressionEvalService — the Lab's verdict pipeline (W2 T4 of the
// DataSource·Expression Lab plan).
//
// Order matters and mirrors the validator's stance: lexical rejections
// (legacy prefix, unknown prefix, invalid accessor) fire BEFORE runtime
// evaluation, so a legacy expression is never "accidentally green" just
// because the runtime alias layer tolerates it on read.
// Runtime = @cogo/runtime-v2 ComputeEvaluator ONLY (v1 is frozen).
// ---------------------------------------------------------------------------
import { ComputeEvaluator, type StateAccessor } from '@cogo/runtime-v2'
import {
  CANONICAL_PREFIXES,
  LEGACY_PREFIX_HINTS,
  findLegacyExpressionPrefixes,
} from '@/features/flow-editor/lib/expression-prefixes'
import { findInvalidAccessors } from '@/features/flow-editor/lib/expression-accessors'

export type CanonicalPrefix = (typeof CANONICAL_PREFIXES)[number]
export type CanonicalContext = Record<CanonicalPrefix, Record<string, unknown>>

export type ExpressionVerdict =
  | 'pass'
  | 'empty'
  | 'legacy-prefix'
  | 'unknown-prefix'
  | 'invalid-accessor'
  | 'runtime-error'

export interface ResolvedInput {
  path: string
  value: unknown
}

export interface ExpressionEvalResult {
  verdict: ExpressionVerdict
  value: unknown
  formattedValue: string
  messages: string[]
  resolvedInputs: ResolvedInput[]
}

export function emptyCanonicalContext(): CanonicalContext {
  return {
    local: {},
    event: {},
    props: {},
    item: {},
    _results: {},
    computed: {},
    appData: {},
    env: {},
    route: { params: {}, path: '/', query: {}, hash: '' },
  }
}

/** Dot-path getter over the seeded context (no writes, no governance). */
class ContextStateAccessor implements StateAccessor {
  private evaluator: ComputeEvaluator | null = null
  constructor(private readonly context: Record<string, unknown>) {}

  bind(evaluator: ComputeEvaluator): void {
    this.evaluator = evaluator
  }

  get(path: string): unknown {
    const segments = path.split('.')
    let cursor: unknown = this.context
    for (const segment of segments) {
      if (cursor == null || typeof cursor !== 'object') return undefined
      cursor = (cursor as Record<string, unknown>)[segment]
    }
    return cursor
  }

  /** Interpolate `text #{expr} text` templates via the bound evaluator. */
  resolve(template: string): unknown {
    if (!this.evaluator) return template
    return template.replace(/#\{([^}]+)\}/g, (_, inner: string) => {
      const v = this.evaluator!.evaluate(inner.trim())
      return v === undefined || v === null ? '' : String(v)
    })
  }

  getSnapshot(): Record<string, unknown> {
    return this.context as Record<string, unknown>
  }
}

// Matches `prefix.path` tokens for known-word prefixes (input tracing).
function collectInputPaths(expression: string, prefixes: readonly string[]): string[] {
  const found: string[] = []
  const re = new RegExp(`\\b(${prefixes.map((p) => p.replace('$', '\\$')).join('|')})\\.[A-Za-z_][\\w.]*`, 'g')
  let m: RegExpExecArray | null
  while ((m = re.exec(expression))) {
    // Trim trailing accessor-call tails: local.name.toUpperCase → keep until
    // the first segment that is followed by "(" in the source.
    const token = m[0]
    if (!found.includes(token)) found.push(token)
  }
  return found
}

// Strip `.method(` call tails from a traced path so we report the DATA path.
function dataPathOf(token: string, expression: string): string {
  const segments = token.split('.')
  for (let i = 1; i < segments.length; i += 1) {
    const head = segments.slice(0, i + 1).join('.')
    const after = expression.indexOf(head) + head.length
    if (expression.slice(after).trimStart().startsWith('(')) {
      return segments.slice(0, i).join('.')
    }
  }
  return token
}

/**
 * Detect the runtime's literal-passthrough fallback: for a PURE wrapped
 * expression (`#{...}` and nothing else), ComputeEvaluator returns the body
 * text unchanged when it cannot interpret a construct (e.g. bare-projection
 * arrows in `.map()`), and `+` string-concat can swallow undefined operands
 * (`right ?? ""`), yielding a truncated source string instead of a value.
 * Reporting either as `pass` would be a lie — the Lab's verdicts must be
 * truthful.
 *
 * Rules (scoped to pure wrapped inputs only):
 *  A. result string === unwrapped body (whitespace-trimmed) → unevaluated.
 *  B. result string still contains expression-source fragments (`=>` or a
 *     `.method(` call tail) → treated as unevaluated.
 * Accepted false-positive edge (W2): a context string VALUE that itself
 * looks like expression source (e.g. local.note = "a => b") trips rule B.
 */
function looksUnevaluated(pureBody: string | null, value: unknown): boolean {
  if (pureBody === null || typeof value !== 'string') return false
  const body = pureBody.trim()
  if (value.trim() === body) return true
  return value.includes('=>') || /\.[a-zA-Z_]\w*\s*\(/.test(value)
}

/**
 * Unwrap a PURE `#{...}` expression, tolerating `}` inside the body (object
 * literals, block bodies). ComputeEvaluator's own unwrap regex
 * (`/^#\{([^}]+)\}$/`) stops at the FIRST `}`, so a braced body like
 * `#{local.items.map(t => ({ v: t.x }))}` never unwraps there and evaluates
 * to garbage — the Lab unwraps greedily here and hands the evaluator the
 * bare body instead. Returns null for anything that is not a single wrapped
 * expression (mixed templates like `a #{x} b` or `#{a} + #{b}` — a `#{`
 * inside the greedy body marks a second wrapper).
 */
function unwrapPureExpression(trimmed: string): string | null {
  const m = /^#\{([\s\S]*)\}$/.exec(trimmed)
  if (!m) return null
  const body = m[1]
  if (!body.trim() || body.includes('#{')) return null
  return body
}

export function evaluateExpression(
  expression: string,
  context: CanonicalContext,
  options: { allowPrefixes?: readonly string[] } = {},
): ExpressionEvalResult {
  const messages: string[] = []
  const trimmed = expression?.trim() ?? ''
  const base: Omit<ExpressionEvalResult, 'verdict'> = {
    value: undefined,
    formattedValue: '',
    messages,
    resolvedInputs: [],
  }

  if (!trimmed) return { verdict: 'empty', ...base }

  // 1) Legacy prefixes — hard reject with migration hints.
  const legacy = findLegacyExpressionPrefixes(trimmed)
  if (legacy.length > 0) {
    for (const prefix of legacy) {
      messages.push(`legacy prefix "${prefix}." → use ${LEGACY_PREFIX_HINTS[prefix]}`)
    }
    return { verdict: 'legacy-prefix', ...base }
  }

  // 2) Unknown prefixes inside #{...} bodies (canonical 9 + explicit itemVar allowance).
  const allowed = new Set<string>([...CANONICAL_PREFIXES, ...(options.allowPrefixes ?? [])])
  const pureBody = unwrapPureExpression(trimmed)
  const bodies: string[] = []
  if (pureBody !== null) {
    bodies.push(pureBody)
  } else {
    // Mixed templates: scan each non-greedy `#{...}` segment. KNOWN W2 edge:
    // a braced body inside a MIXED template is truncated at its first `}` by
    // this regex — pure wrapped inputs (the Lab's normal case) are covered by
    // the greedy unwrap above.
    const bodyRe = /#\{([^}]+)\}/g
    let bodyMatch: RegExpExecArray | null
    while ((bodyMatch = bodyRe.exec(trimmed))) bodies.push(bodyMatch[1])
  }
  const unknown = new Set<string>()
  for (const body of bodies) {

    // Pre-scan arrow-parameter declarations with their positions, so the
    // exclusion below is declaration-before-usage scoped instead of testing
    // the whole body at once (which previously excluded a name the instant
    // ANY arrow anywhere in the body reused it, even before the arrow ran —
    // see the regression test for `task.value + items.map(task => task.x)`).
    const arrowDeclRe = /\(([\w\s,]*)\)\s*=>|([A-Za-z_$][\w$]*)\s*=>/g
    const arrowDecls: Array<{ name: string; index: number }> = []
    let arrowMatch: RegExpExecArray | null
    while ((arrowMatch = arrowDeclRe.exec(body))) {
      const declIndex = arrowMatch.index
      const params = arrowMatch[1] !== undefined ? arrowMatch[1].split(',') : [arrowMatch[2]]
      for (const raw of params) {
        const name = raw?.trim()
        if (name) arrowDecls.push({ name, index: declIndex })
      }
    }

    const headRe = /(?:^|[^.\w$])([A-Za-z_$][\w$]*)\s*\./g
    let head: RegExpExecArray | null
    while ((head = headRe.exec(body))) {
      const name = head[1]
      if (name === 'Math') continue
      // Arrow-callback params (`i => i.done`) are locals, not prefixes — but
      // only for occurrences that come AFTER the arrow that declared them
      // (a callback param can only be used after its own declaration).
      // KNOWN LIMITATION (accepted W2 scope): this is a linear "declared
      // before this index" check, not real paren-matched scoping, so a
      // genuinely-unknown usage AFTER an arrow that happens to declare the
      // same name — but OUTSIDE that arrow's body, e.g.
      // `#{items.map(task => task.x) + task.value}` — is still (wrongly)
      // excluded here. We don't attempt paren-matching to find the arrow's
      // true body end. On its own this lexical miss CAN reach a false green:
      // the runtime's literal-passthrough fallback plus `+` string-concat
      // (which coerces an undefined operand to "") can turn such an
      // expression into a non-undefined string result. The unevaluated-
      // passthrough detection in step 4 below closes that traced silent-pass
      // class; the residual accepted edge is a context string VALUE that
      // itself looks like expression source (see looksUnevaluated).
      const isArrowParam = arrowDecls.some((decl) => decl.name === name && decl.index < head!.index)
      if (isArrowParam) continue
      if (!allowed.has(name)) unknown.add(name)
    }
  }
  if (unknown.size > 0) {
    messages.push(
      `unknown prefix ${[...unknown].map((p) => `"${p}."`).join(', ')} — canonical: ${CANONICAL_PREFIXES.join(', ')} (itemVar scopes must be declared)`,
    )
    return { verdict: 'unknown-prefix', ...base }
  }

  // 3) Invalid accessors — vocabulary from the parity-locked mirror.
  const badAccessors = findInvalidAccessors(trimmed)
  if (badAccessors.length > 0) {
    messages.push(`unsupported accessor: ${badAccessors.map((a) => `.${a}()`).join(', ')}`)
    return { verdict: 'invalid-accessor', ...base }
  }

  // 4) Runtime evaluation over the seeded context.
  const accessor = new ContextStateAccessor(context)
  const evaluator = new ComputeEvaluator(accessor)
  accessor.bind(evaluator)

  const inputTokens = collectInputPaths(trimmed, [...allowed])
  const resolvedInputs: ResolvedInput[] = inputTokens.map((token) => {
    const path = dataPathOf(token, trimmed)
    return { path, value: accessor.get(path) }
  })

  try {
    // Hand the evaluator the unwrapped body for pure expressions — its own
    // unwrap regex cannot cross a `}` inside the body (see unwrapPureExpression).
    const value = evaluator.evaluate(pureBody ?? trimmed)
    if (value === undefined) {
      messages.push(
        'expression evaluated to undefined — most likely a wrong input path (see resolved inputs); if undefined is the expected result, treat this as informational',
      )
      return { verdict: 'runtime-error', ...base, resolvedInputs }
    }
    if (looksUnevaluated(pureBody, value)) {
      messages.push(
        'evaluator returned the expression unevaluated — this construct is not supported by the runtime',
      )
      return { verdict: 'runtime-error', ...base, resolvedInputs }
    }
    return {
      verdict: 'pass',
      value,
      formattedValue: JSON.stringify(value) ?? String(value),
      messages,
      resolvedInputs,
    }
  } catch (error) {
    messages.push(error instanceof Error ? error.message : String(error))
    return { verdict: 'runtime-error', ...base, resolvedInputs }
  }
}
